import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { UtensilsCrossed, Calendar, MapPin } from 'lucide-react';
import AppLogo from '../components/AppLogo';
import AddIntegrationDialog, { MEAL_SERVICES } from '../components/integrations/AddIntegrationDialog';
import ScheduleMealDialog from '../components/integrations/ScheduleMealDialog';

const IntegrationsPage = () => {
  const { circleId } = useParams();
  const navigate = useNavigate();
  const [integrations, setIntegrations] = useState([]);
  const [mealOrders, setMealOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addIntegrationDialogOpen, setAddIntegrationDialogOpen] = useState(false);
  const [orderMealDialogOpen, setOrderMealDialogOpen] = useState(false);
  const [newIntegration, setNewIntegration] = useState({
    integration_type: 'meal_delivery',
    service_name: 'ubereats',
    config: { api_key: '', preferences: '' }
  });
  const [newMealOrder, setNewMealOrder] = useState({
    integration_id: '',
    delivery_address: '',
    delivery_time: '',
    notes: ''
  });

  useEffect(() => {
    fetchData();
  }, [circleId]);

  const fetchData = async () => {
    try {
      const [integrationsRes, ordersRes] = await Promise.all([
        axios.get(`${API}/circles/${circleId}/integrations`),
        axios.get(`${API}/circles/${circleId}/meal-orders`)
      ]);

      setIntegrations(integrationsRes.data);
      setMealOrders(ordersRes.data);
    } catch (error) {
      toast.error('Failed to fetch integrations');
    } finally {
      setLoading(false);
    }
  };

  const handleAddIntegration = async (e) => {
    e.preventDefault();
    try {
      await axios.post(
        `${API}/integrations`,
        { ...newIntegration, circle_id: circleId }
      );
      toast.success('Integration added!');
      setAddIntegrationDialogOpen(false);
      setNewIntegration({
        integration_type: 'meal_delivery',
        service_name: 'ubereats',
        config: { api_key: '', preferences: '' }
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add integration');
    }
  };

  const handleToggleIntegration = async (integrationId, currentState) => {
    try {
      await axios.put(
        `${API}/integrations/${integrationId}`,
        { is_enabled: !currentState },
        { params: { is_enabled: !currentState }
        }
      );
      setIntegrations(integrations.map(i => 
        i.id === integrationId ? { ...i, is_enabled: !currentState } : i
      ));
      toast.success(`Integration ${!currentState ? 'enabled' : 'disabled'}`);
    } catch (error) {
      toast.error('Failed to update integration');
    }
  };

  const handleOrderMeal = async (e) => {
    e.preventDefault();
    try {
      await axios.post(
        `${API}/meal-orders`,
        { ...newMealOrder, circle_id: circleId }
      );
      toast.success('Meal order scheduled!');
      setOrderMealDialogOpen(false);
      setNewMealOrder({
        integration_id: '',
        delivery_address: '',
        delivery_time: '',
        notes: ''
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to schedule meal');
    }
  };

  const getServiceIcon = (serviceName) => {
    const service = MEAL_SERVICES.find(s => s.value === serviceName);
    return service ? service.icon : '\u{1F37D}\uFE0F';
  };

  const getOrderStatusClass = (status) => {
    if (status === 'delivered') return 'bg-emerald-100 text-cyan-600';
    if (status === 'confirmed') return 'bg-cyan-100 text-cyan-700';
    return 'bg-amber-100 text-amber-700';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-emerald-700"></div>
      </div>
    );
  }

  const enabledMealIntegrations = integrations.filter(i => i.integration_type === 'meal_delivery' && i.is_enabled);

  return (
    <div className="min-h-screen" data-testid="integrations-page">
      {/* Header */}
      <div className="surface-header">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <AppLogo />
              <div>
                <h1 className="text-2xl font-bold text-foreground">Circle Integrations</h1>
                <p className="text-sm text-muted-foreground">Optional plugins to coordinate care support</p>
              </div>
            </div>

            <AddIntegrationDialog
              open={addIntegrationDialogOpen}
              onOpenChange={setAddIntegrationDialogOpen}
              integration={newIntegration}
              setIntegration={setNewIntegration}
              onSubmit={handleAddIntegration}
            />
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Active Integrations */}
          <div className="lg:col-span-2 space-y-6">
            <Card className="surface-card ">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <UtensilsCrossed className="w-5 h-5 text-cyan-600" />
                  Active Integrations
                </CardTitle>
                <CardDescription>Manage your circle's integrations</CardDescription>
              </CardHeader>
              <CardContent>
                {integrations.length > 0 ? (
                  <div className="space-y-3">
                    {integrations.map((integration) => (
                      <div key={integration.id} className="bg-muted/30 rounded-lg p-4" data-testid={`integration-${integration.id}`}>
                        <div className="flex items-start justify-between">
                          <div className="flex items-start gap-3 flex-1">
                            <span className="text-3xl">{getServiceIcon(integration.service_name)}</span>
                            <div className="flex-1">
                              <h4 className="font-semibold text-foreground capitalize">
                                {MEAL_SERVICES.find(s => s.value === integration.service_name)?.label || integration.service_name}
                              </h4>
                              <p className="text-sm text-muted-foreground capitalize">{integration.integration_type.replace('_', ' ')}</p>
                              {integration.config.preferences && (
                                <p className="text-xs text-muted-foreground mt-1">{integration.config.preferences}</p>
                              )}
                            </div>
                          </div>
                          <Switch
                            checked={integration.is_enabled}
                            onCheckedChange={() => handleToggleIntegration(integration.id, integration.is_enabled)}
                            data-testid={`toggle-integration-${integration.id}`}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <UtensilsCrossed className="w-16 h-16 text-muted-foreground/30 mx-auto mb-4" />
                    <p className="text-muted-foreground mb-4">No integrations added yet</p>
                    <Button
                      size="sm"
                      onClick={() => setAddIntegrationDialogOpen(true)}
                      className="btn-primary"
                    >
                      Add Your First Integration
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Meal Orders */}
            <Card className="surface-card ">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Calendar className="w-5 h-5 text-emerald-600" />
                      Scheduled Meals
                    </CardTitle>
                    <CardDescription>Coordinate meal deliveries for your circle</CardDescription>
                  </div>
                  {enabledMealIntegrations.length > 0 && (
                    <ScheduleMealDialog
                      open={orderMealDialogOpen}
                      onOpenChange={setOrderMealDialogOpen}
                      order={newMealOrder}
                      setOrder={setNewMealOrder}
                      enabledIntegrations={enabledMealIntegrations}
                      getServiceIcon={getServiceIcon}
                      onSubmit={handleOrderMeal}
                    />
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {mealOrders.length > 0 ? (
                  <div className="space-y-3">
                    {mealOrders.map((order) => (
                      <div key={order.id} className="bg-muted/30 rounded-lg p-4" data-testid={`meal-order-${order.id}`}>
                        <div className="flex items-start gap-3">
                          <span className="text-2xl">{getServiceIcon(order.integration.service_name)}</span>
                          <div className="flex-1">
                            <div className="flex items-start justify-between">
                              <div>
                                <h4 className="font-semibold text-foreground">
                                  {MEAL_SERVICES.find(s => s.value === order.integration.service_name)?.label} Delivery
                                </h4>
                                <p className="text-sm text-muted-foreground mt-1">Scheduled by {order.user.name}</p>
                              </div>
                              <span className={`px-3 py-1 text-xs font-medium rounded-full ${getOrderStatusClass(order.status)}`}>
                                {order.status}
                              </span>
                            </div>
                            <div className="mt-3 space-y-1 text-sm">
                              <div className="flex items-start gap-2 text-muted-foreground">
                                <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0" />
                                <span>{order.delivery_address}</span>
                              </div>
                              {order.delivery_time && (
                                <div className="flex items-center gap-2 text-muted-foreground">
                                  <Calendar className="w-4 h-4 flex-shrink-0" />
                                  <span>{new Date(order.delivery_time).toLocaleString()}</span>
                                </div>
                              )}
                              {order.notes && (
                                <p className="text-muted-foreground text-xs mt-2">{order.notes}</p>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Calendar className="w-16 h-16 text-muted-foreground/30 mx-auto mb-4" />
                    <p className="text-muted-foreground">No meals scheduled yet</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Sidebar - Info */}
          <div className="space-y-6">
            <Card className="surface-card ">
              <CardHeader>
                <CardTitle className="text-lg">About Integrations</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 text-sm text-foreground">
                <p>
                  Optional plugins help coordinate practical support for your circle members.
                </p>
                <div className="space-y-2">
                  <h4 className="font-semibold text-foreground">Meal Delivery</h4>
                  <p className="text-xs text-muted-foreground">
                    Connect popular meal delivery services to schedule and coordinate meals. Circle members can see scheduled deliveries and add their own.
                  </p>
                </div>
                <div className="space-y-2">
                  <h4 className="font-semibold text-foreground">Coming Soon</h4>
                  <ul className="text-xs text-muted-foreground space-y-1">
                    <li>• Transportation (Uber, Lyft)</li>
                    <li>• Pharmacy deliveries</li>
                    <li>• Grocery delivery</li>
                  </ul>
                </div>
                <div className="bg-primary/10 rounded-lg p-3 mt-4">
                  <p className="text-xs text-foreground">
                    <strong>Note:</strong> Currently for coordination only. Manual ordering required. Full automation coming soon!
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default IntegrationsPage;
